import React, { useEffect, useRef, useState } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
// Phase 2B: video calls go through the BE proxy via elearningApi.video*.
import elearningApi from 'api/elearningApi';
import VideoPlayer from '../components/VideoPlayer';
import useSingleTab from 'hooks/useSingleTab';
import { toastWrapper } from 'utils';
import LoadingSpinner from '../components/LoadingSpinner';
import TimeExceedWarning from '../components/TimeExceedWarning';
import { useSelector } from 'react-redux';
import { selectElearningData } from 'store/elearning.slice';
import useMediaQuery from 'hooks/useMediaQuery';
import BackButton from 'components/BackButton';
import {
  CAP_CLEARED_EVENT, isCapSuspendedError, notifyDailyLimitReached, responseHitDailyLimit,
} from 'utils/daily-limit-event.util';

function ElearningStudentVideoPage() {
  const { id: videoId } = useParams();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('m');
  const [videoInstance, setVideoInstance] = useState(null);
  const [videoView, setVideoView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const elearningData = useSelector(selectElearningData);
  const { isLimitExceeded, timeLimitPerDay, totalTodayTime, elearningUser } = elearningData;
  const centerLogoUrl = elearningUser?.center?.logo;
  const isMobile = useMediaQuery('(max-width: 768px)');
  // Mirror Redux `isLimitExceeded` để guard tick in-flight cuối cùng của
  // VideoPlayer interval (15s) — vẫn có thể fire 1 lần ngay trước khi
  // React commit unmount sau khi server-truth lock flip.
  const isLimitExceededRef = useRef(isLimitExceeded);
  // 403 CAP_SUSPENDED → dừng gửi; overlay đóng (sang ngày / "Để sau") → thử lại.
  const capSuspendedRef = useRef(false);
  useEffect(() => {
    const onCleared = () => { capSuspendedRef.current = false; };
    window.addEventListener(CAP_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(CAP_CLEARED_EVENT, onCleared);
  }, []);
  isLimitExceededRef.current = isLimitExceeded;

  useSingleTab('/elearning/student/video', 'elearning_module');

  useEffect(() => {
    if (!videoId || !moduleId) return;

    setVideoInstance(null);
    setVideoView(null);
    setLoading(true);
    setError(null);

    elearningApi
      .videoInstance(videoId)
      .then((data) => {
        setVideoInstance(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching video module:', error);
        setError('Không thể tải video. Vui lòng thử lại sau.');
        setLoading(false);
      });

    elearningApi
      .videoView(moduleId)
      .then((data) => {
        setVideoView(data?.at(-1));
      })
      .catch((error) => {
        console.error('Error fetching video view:', error);
      });
  }, [videoId, moduleId]);

  // Trả về { mapa, percent } server dựng để VideoPlayer hiển thị — percent do
  // server tính (không bao giờ thấp hơn cũ), FE không tự kẹp nữa.
  const onHeartbeat = async ({ currentTime, duration, indices }) => {
    if (!videoView?.id) return null;
    // Server-truth lock đã flip — chặn save dù VideoPlayer interval còn 1 tick
    // sót lại trước khi React unmount.
    if (isLimitExceededRef.current) return null;
    // Đang bị khoá (403 CAP_SUSPENDED) — overlay đã báo, không gửi tiếp.
    if (capSuspendedRef.current) return null;

    try {
      const res = await elearningApi.videoUpdateView(videoView.id, {
        currentTime,
        duration,
        indices,
        // Lấy cmid từ chính bản ghi view (đi cặp với viewId) — đọc `moduleId`
        // từ URL thì lúc đổi video bằng back/forward có 1 lượt render mang
        // viewId cũ + cmid mới, lượt gửi bù của video cũ bị server từ chối.
        cmid: Number(videoView.cm_id ?? moduleId),
        videoInstanceId: videoInstance?.id,
      });
      if (responseHitDailyLimit(res)) notifyDailyLimitReached();
      return res;
    } catch (error) {
      // 429 = 2 lượt gửi quá sát nhau — các ô vẫn được gửi lại lượt sau,
      // không phải lỗi cần HV tải lại trang.
      if (error?.response?.status === 429) return null;
      // Bị khoá: dừng gửi, không toast "tải lại" (overlay khoá đã báo).
      if (isCapSuspendedError(error)) {
        capSuspendedRef.current = true;
        return null;
      }
      console.error('Error updating video view:', error);
      toastWrapper(
        'Cập nhật thời gian xem không thành công. Vui lòng tải lại trình duyệt.',
        'error'
      );
      return null;
    }
  };

   if (isLimitExceeded) {
     return (
       <TimeExceedWarning
         timeLimitPerDay={timeLimitPerDay}
         totalTodayTime={totalTodayTime}
       />
     );
   }

  if ((loading || !videoInstance || !videoView) && !error) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ height: '100vh', overflowY: 'scroll' }}>
      <Container className='my-2'>
        {error && <Alert variant='danger'>{error}</Alert>}
        <BackButton />
        {videoInstance && videoInstance?.videourl && videoView && (
          <div className='mx-auto' style={{width: isMobile ? '100%' : '75%'}}>
            <h3 className='mb-4'>{videoInstance.name}</h3>
            <VideoPlayer
                key={videoView.id}
                url={videoInstance.videourl}
                videoView={videoView}
                onHeartbeat={onHeartbeat}
                intervalTime={15}
                logoUrl={centerLogoUrl}
              />
          </div>
        )}
      </Container>
    </div>
  );
}

export default ElearningStudentVideoPage;
